import { useDispatch, useSelector } from "react-redux";
import { PaidServicesTooltip } from "@/components/PaidServicesTooltip";
import {
  COMMERCIAL_CREDIT_CHECK_COPY,
  COMMERCIAL_CREDIT_CHECKS,
  DOCUMENT_TYPES,
  ERRORS_MESSAGE,
  SERVICE_PROVIDER_LOGOS,
  type CommercialCreditCheck,
} from "@/constants";
import { usePaidServices } from "@/hooks";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
// @ts-expect-error: ignoring as it is existing js file
import { getEntityCommercialCreditCheck, userDetailsSelector } from "@/store/slices/applicationFormSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import type { ApplicationFormSlice, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
import type { UserPermissionsDocument, UserSlice } from "@/store/slices/types/userSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userPermissionsSelector } from "@/store/slices/userSlice";
import type { AppDispatch } from "@/store/store";
import { getErrorToastPayload, getSuccessToastPayload } from "@/utils/commonFunctions";
import type { ApplicationAccessComponents, IDispatchResponse } from "@/utils/interface";
import { EntityServiceCardGroup, type EntityServiceCardProps } from "../EntityServiceCard";

interface CommercialCreditCheckAccess {
  check: CommercialCreditCheck;
  /** Tenant-level switch in `platformUIComponentAccess`. */
  flag: keyof ApplicationAccessComponents["entity"];
  /** The user's service permission, the same one the server's `permissionGuard` checks. */
  permission: keyof UserPermissionsDocument["services"];
  /** The name the server gives the PDF it attaches. Once the entity has that document, the check is not offered. */
  documentName: (typeof DOCUMENT_TYPES)[keyof typeof DOCUMENT_TYPES];
}

/** In display order. */
const CHECK_ACCESS: CommercialCreditCheckAccess[] = [
  {
    check: COMMERCIAL_CREDIT_CHECKS.REPORT,
    flag: "isEntityCreditReportEnabled",
    permission: "commercial_credit_report",
    documentName: DOCUMENT_TYPES.COMMERCIAL_CREDIT_REPORT,
  },
  {
    check: COMMERCIAL_CREDIT_CHECKS.SCORE,
    flag: "isEntityCreditScoreEnabled",
    permission: "commercial_credit_score",
    documentName: DOCUMENT_TYPES.COMMERCIAL_CREDIT_SCORE,
  },
];

export interface EntityCommercialCreditChecksProps {
  entity: { _id: string; acn?: string; documents?: { name?: string }[] };
  applicationId: string;
  isDisableEdit?: boolean;
}

/**
 * The CreditorWatch credit score and credit report checks for an entity. Each attaches a PDF to the entity, and is
 * offered only until that PDF is there.
 */
const EntityCommercialCreditChecks = ({
  entity,
  applicationId,
  isDisableEdit = false,
}: EntityCommercialCreditChecksProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { canUsePaidServices } = usePaidServices();
  const { accessComponents } = useAccessAppComponent();
  const permissions = useSelector<UserSlice, UserPermissionsDocument | undefined>(userPermissionsSelector);
  const { gettingCommercialCreditChecks } = useSelector<ApplicationFormSlice, UserDetailsState>(userDetailsSelector);

  const { _id: entityId, acn, documents } = entity;
  const entityAccess = accessComponents.application.entity;
  const loadingChecks = gettingCommercialCreditChecks[entityId];
  const attachedDocumentNames = new Set(documents?.map((doc) => doc.name));

  // The server looks the company up by ACN, so there is nothing to offer without one.
  if (!acn) return null;

  const handleRunCheck = async (check: CommercialCreditCheck) => {
    const res: IDispatchResponse = await dispatch(
      getEntityCommercialCreditCheck({ check, applicationId, entityId, acn }),
    );

    dispatch(
      openSnackbar(
        res?.error
          ? getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)
          : getSuccessToastPayload(res, COMMERCIAL_CREDIT_CHECK_COPY[check].successMessage),
      ),
    );
  };

  const cards: EntityServiceCardProps[] = CHECK_ACCESS.filter(
    ({ flag, permission, documentName }) =>
      entityAccess[flag] && permissions?.services?.[permission] && !attachedDocumentNames.has(documentName),
  ).map(({ check }) => {
    const { title, subtitle, loadingSubtitle } = COMMERCIAL_CREDIT_CHECK_COPY[check];

    return {
      id: check,
      logos: [SERVICE_PROVIDER_LOGOS.creditorWatch],
      title,
      subtitle,
      loadingSubtitle,
      isLoading: Boolean(loadingChecks?.[check]),
      isDisabled: !canUsePaidServices || isDisableEdit,
      tooltip: canUsePaidServices ? null : <PaidServicesTooltip />,
      onClick: () => handleRunCheck(check),
    };
  });

  return <EntityServiceCardGroup cards={cards} />;
};

export default EntityCommercialCreditChecks;
